import React, { useState, useEffect } from 'react';
import { usersAPI, timeEntriesAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import { User, Users, Mail, Edit2, Check, X, Clock, AlertCircle, CheckCircle, Shield } from 'lucide-react';

const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'Europe/Paris', label: 'Europe (Paris)' },
  { value: 'Asia/Dubai', label: 'UAE (Dubai)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Australia/Sydney', label: 'Australia (Sydney)' },
  { value: 'UTC', label: 'UTC' },
];
const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_.-]{2,49}$/;

const Profile = () => {
  const { user, setUser } = useAuthStore();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ totalHours: 0, pending: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchProfile();
    fetchStats();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await usersAPI.getMyProfile();
      setProfile(res.data.data);
      setNewUsername(res.data.data.username || '');
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const res = await timeEntriesAPI.list({
        start_date: startOfMonth.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
      });
      const entries = res.data.data.entries || [];
      setStats({
        totalHours: entries.filter(e => (e.activity_type || '').toUpperCase() !== 'HOLIDAY').reduce((sum, e) => sum + parseFloat(e.hours || 0), 0),
        pending: entries.filter(e => e.status === 'PENDING').length,
        approved: entries.filter(e => e.status === 'APPROVED').length,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) return;
    const normalizedUsername = newUsername.trim().toLowerCase();
    if (!USERNAME_PATTERN.test(normalizedUsername)) {
      setUsernameStatus({
        type: 'error',
        message: 'Use 3-50 characters: letters, numbers, dots, underscores, and hyphens only.',
      });
      setTimeout(() => setUsernameStatus({ type: '', message: '' }), 4000);
      return;
    }
    try {
      await usersAPI.updateMyUsername(normalizedUsername);
      setProfile(p => ({ ...p, username: normalizedUsername }));
      setUsernameStatus({ type: 'success', message: 'Username updated successfully!' });
      setEditingUsername(false);
      // Update the local auth store too
      const updatedUser = { ...user, username: normalizedUsername };
      if (typeof setUser === 'function') {
        try { setUser(updatedUser); } catch (_) {}
      }
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setTimeout(() => setUsernameStatus({ type: '', message: '' }), 3000);
    } catch (err) {
      console.error('Error updating username:', err);
      setUsernameStatus({ type: 'error', message: err.response?.data?.detail || err.response?.data?.message || 'Username update failed.' });
      setTimeout(() => setUsernameStatus({ type: '', message: '' }), 4000);
    }
  };

  const handleUpdateTimezone = async (tz) => {
    try {
      await usersAPI.updateMyTimezone(tz);
      setProfile(p => ({ ...p, timezone: tz }));
      setUsernameStatus({ type: 'success', message: 'Timezone updated!' });
      // Update store/localStorage
      const updatedUser = { ...user, timezone: tz };
      if (typeof setUser === 'function') {
        try { setUser(updatedUser); } catch (_) {}
      }
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setTimeout(() => setUsernameStatus({ type: '', message: '' }), 3000);
    } catch (err) {
      console.error('Error updating timezone:', err);
      setUsernameStatus({ type: 'error', message: err.response?.data?.detail || err.response?.data?.message || 'Failed to update timezone' });
      setTimeout(() => setUsernameStatus({ type: '', message: '' }), 4000);
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  // Determine if the current user is a manager/admin
  const isManagerOrAdmin = profile?.roles?.some(r => r.value === 'Manager' || r.value === 'Admin');
  const directReports = profile?.direct_reports || [];

  if (loading) return (
    <div className="loading-container"><div className="spinner"></div></div>
  );

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>My Profile</h2>
          <p>Your personal information and activity summary</p>
        </div>
      </header>

      <div className="page-content">
        {/* Profile Card */}
        <div className="card" style={{ marginBottom: '24px', overflow: 'hidden' }}>
          <div style={{
            background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
            padding: '32px 32px 60px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', bottom: '-36px', left: '32px',
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'white', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '26px', fontWeight: 700,
              color: 'var(--primary)', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              border: '3px solid white',
            }}>
              {getInitials(profile?.name)}
            </div>
          </div>

          <div className="card-body" style={{ paddingTop: '52px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Name */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Full Name</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{profile?.name}</div>
              </div>

              {/* Username */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Username</div>
                {editingUsername ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      className="form-input"
                      value={newUsername}
                      onChange={e => setNewUsername(e.target.value)}
                      style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '8px', maxWidth: '180px' }}
                      autoFocus
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleSaveUsername}><Check size={14} /></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingUsername(false); setNewUsername(profile?.username || ''); }}><X size={14} /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--primary)', fontFamily: 'monospace' }}>@{profile?.username || 'not set'}</span>
                    <button className="btn btn-icon" style={{ padding: '4px' }} onClick={() => setEditingUsername(true)} title="Edit username">
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
                {usernameStatus.message && (
                  <p style={{ fontSize: '12px', color: usernameStatus.type === 'success' ? 'var(--success)' : 'var(--error)', marginTop: '4px' }}>
                    {usernameStatus.message}
                  </p>
                )}
                {editingUsername && !usernameStatus.message && (
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Allowed: letters, numbers, <code>.</code>, <code>_</code>, <code>-</code>. No spaces or <code>@</code>.
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Email</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--text-primary)' }}>
                  <Mail size={14} style={{ color: 'var(--text-secondary)' }} />
                  {profile?.email}
                </div>
              </div>

              {/* Timezone */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Preferred Timezone</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select 
                    className="form-select" 
                    value={profile?.timezone || 'Asia/Kolkata'} 
                    onChange={e => handleUpdateTimezone(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: '13px', height: '32px', borderRadius: '8px', maxWidth: '200px' }}
                  >
                    {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Manager */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Reporting Manager</div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{profile?.manager_name || '—'}</div>
              </div>

              {/* Roles */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Roles</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {profile?.roles?.map(r => (
                    <span key={r.id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '4px 12px', borderRadius: '20px',
                      background: 'rgba(13,148,136,0.1)', color: 'var(--primary)', fontWeight: 600, fontSize: '12px',
                    }}>
                      <Shield size={11} /> {r.value}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Direct Reports — shown only for managers/admins */}
        {isManagerOrAdmin && (
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Users size={18} style={{ color: 'var(--primary)' }} />
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                  Direct Reports
                  <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    ({directReports.length})
                  </span>
                </h3>
              </div>
              {directReports.length === 0 ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>No direct reports assigned.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                  {directReports.map(dr => (
                    <div key={dr.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', borderRadius: '10px',
                      border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                    }}>
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 700, fontSize: '13px', flexShrink: 0,
                      }}>
                        {(dr.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dr.name}</div>
                        {dr.username && (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>@{dr.username}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* This Month Stats */}
        <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: 'var(--text-primary)' }}>This Month's Activity</h3>
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon teal"><Clock size={22} /></div>
            </div>
            <div className="stat-value">{stats.totalHours.toFixed(1)}h</div>
            <div className="stat-label">Hours Logged</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon green"><CheckCircle size={22} /></div>
            </div>
            <div className="stat-value">{stats.approved}</div>
            <div className="stat-label">Approved Entries</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon orange"><AlertCircle size={22} /></div>
            </div>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending Approval</div>
          </div>
        </div>

        {/* Login tip */}
        <div style={{ padding: '16px 20px', background: 'rgba(13,148,136,0.05)', borderRadius: '12px', border: '1px solid rgba(13,148,136,0.12)', fontSize: '13px', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--primary)' }}>💡 Tip:</strong> You can log in using either your <strong>email address</strong> or your <strong>username</strong> (<code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>@{profile?.username || 'username'}</code>).
        </div>
      </div>
    </div>
  );
};

export default Profile;
